import { baseUniDriverFactory } from '../utils/test-utils/unidriver';

import {
  buttonDriverFactory,
  ButtonUniDriver,
} from '../Button/Button.uni.driver';
import { tooltipDriverFactory } from '../Tooltip/Tooltip.uni.driver';
import { dataHooks } from './constants';
import { getFormattedDataHooks } from '../utils/test-utils';
import { UniDriver } from '@wix/unidriver-core';

export type BaseModalLayoutUniDriver = ReturnType<
  typeof baseModalLayoutDriverFactory
>;
const fDataHooks = getFormattedDataHooks(dataHooks);

export const baseModalLayoutDriverFactory = (
  base: UniDriver,
  body?: UniDriver,
) => {
  const getButtonDriver = (dataHook: string) => {
    return buttonDriverFactory(base.$(`[data-hook-internal="${dataHook}"]`));
  };

  return {
    ...baseUniDriverFactory(base),
    /**
     * Gets the modal theme
     */
    getTheme: async () => base.attr('data-skin'),
    /**
     * Gets the modal skin
     */
    getSkin: async (): Promise<string | null> => base.attr('data-skin'),
    /**
     * Clicks the modal close-button
     */
    clickCloseButton: async (): Promise<void> =>
      base.$(fDataHooks.closeButton).click(),
    /**
     * Clicks the modal help-button
     */
    clickHelpButton: async (): Promise<void> =>
      base.$(fDataHooks.helpButton).click(),
    /**
     * Checks whether a node with the provided dataHook exists
     */
    childExists: async (dataHook: string): Promise<boolean> =>
      base.$(`[data-hook="${dataHook}"]`).exists() ||
      base.$(`[data-hook-internal="${dataHook}"]`).exists(),
    /**
     * Gets the title's text
     */
    getTitleText: async (): Promise<string> =>
      base.$(fDataHooks.headerTitle).text(),
    /**
     * Gets the subtitle's text
     */
    getSubtitleText: async (): Promise<string> =>
      base.$(fDataHooks.headerSubtitle).text(),

    /**
     * Gets the secondary button driver
     */
    secondaryButtonDriver: getButtonDriver(dataHooks.footerSecondaryButton),

    /**
     * @deprecated use secondaryButtonDriver
     * Gets the secondary button driver
     */
    getSecondaryButtonDriver: async (): Promise<ButtonUniDriver> =>
      getButtonDriver(dataHooks.footerSecondaryButton),

    /**
     * Gets the primary button driver
     */
    primaryButtonDriver: getButtonDriver(dataHooks.footerPrimaryButton),

    /**
     * @deprecated use primaryButtonDriver
     * Gets the primary button driver
     */
    getPrimaryButtonDriver: async (): Promise<ButtonUniDriver> =>
      getButtonDriver(dataHooks.footerPrimaryButton),
    /**
     * Gets the primary button's tooltip content
     */

    getPrimaryButtonTooltipContent: async (): Promise<string | null> => {
      const tooltipDriver = tooltipDriverFactory(
        base.$(fDataHooks.primaryButtonTooltip),
        body ?? base,
      );
      return tooltipDriver.getTooltipText();
    },
    /**
     * Gets the illustration source
     */
    getIllustrationSrc: async (): Promise<string | null> =>
      base.$(fDataHooks.illustrationSrc).attr('src'),
  };
};
